/**
 * Scintigraphy colormap — 256-entry RGB lookup table.
 *
 * Linearly interpolates between 5 control points:
 *   0: Black (0, 0, 0)
 *  64: Teal  (0, 128, 128)
 * 128: Purple (128, 0, 128)
 * 192: Orange (255, 165, 0)
 * 255: White  (255, 255, 255)
 */

type RGB256 = [number, number, number];

const CONTROL_POINTS: Array<[index: number, color: RGB256]> = [
    [0, [0, 0, 0]],
    [64, [0, 128, 128]],
    [128, [128, 0, 128]],
    [192, [255, 165, 0]],
    [255, [255, 255, 255]],
];

let cached: RGB256[] | null = null;

/**
 * Returns a 256-entry scintigraphy colormap.
 * Each entry is an RGB triple with components in 0-255.
 * The result is cached after first computation.
 */
export function getScintigraphyColormap(): RGB256[] {
    if (cached) return cached;

    const lut: RGB256[] = new Array(256);

    for (let i = 0; i < 256; i++) {
        // Find surrounding control points
        let lo = 0;
        for (let c = 1; c < CONTROL_POINTS.length; c++) {
            if (CONTROL_POINTS[c][0] >= i) {
                lo = c - 1;
                break;
            }
        }
        const hi = lo + 1;

        const [iLo, cLo] = CONTROL_POINTS[lo];
        const [iHi, cHi] = CONTROL_POINTS[hi];
        const t = (i - iLo) / (iHi - iLo);

        lut[i] = [
            Math.round(cLo[0] + t * (cHi[0] - cLo[0])),
            Math.round(cLo[1] + t * (cHi[1] - cLo[1])),
            Math.round(cLo[2] + t * (cHi[2] - cLo[2])),
        ];
    }

    cached = lut;
    return lut;
}
